import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import type { ReactNode } from "react";
import { useId } from "react";

import { cn } from "../lib/cn";
import { FOCUS_RING, REDUCED_MOTION_SAFE } from "../lib/focus";
import { omitUndefined } from "../lib/optional";

/** Radix models the third state as the literal string, not as `null`. */
export type typeCheckboxState = boolean | "indeterminate";

export type typeCheckboxProps = {
  label: string;
  /** Supporting sentence under the label — what checking this actually does. */
  description?: string | undefined;
  checked?: typeCheckboxState | undefined;
  defaultChecked?: typeCheckboxState | undefined;
  onCheckedChange?: ((checked: typeCheckboxState) => void) | undefined;
  disabled?: boolean | undefined;
  required?: boolean | undefined;
  name?: string | undefined;
  value?: string | undefined;
  /** Inline validation, under the control. */
  error?: string | undefined;
  className?: string | undefined;
};

const CheckIcon = () => (
  <svg
    className="h-icon-sm w-icon-sm"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="m3 8.5 3.5 3.5L13 5"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IndeterminateIcon = () => (
  <svg
    className="h-icon-sm w-icon-sm"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M4 8h8"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * A checkbox with its label.
 *
 * The box itself is 24px so it reads at arm's length, and the whole
 * label-plus-box row is the hit area, which is what actually gets tapped. The
 * row clears 44px on its own without hit-slop.
 */
export const Checkbox = ({
  label,
  description,
  checked,
  defaultChecked,
  onCheckedChange,
  disabled = false,
  required = false,
  name,
  value,
  error,
  className,
}: typeCheckboxProps): ReactNode => {
  const generatedId = useId();
  const boxId = `${generatedId}-box`;
  const descriptionId = `${generatedId}-description`;
  const errorId = `${generatedId}-error`;
  const invalid = typeof error === "string" && error.length > 0;

  return (
    <div className="flex w-full flex-col gap-1">
      <label
        htmlFor={boxId}
        className={cn(
          "flex min-h-touch-target w-full cursor-pointer items-center gap-3 py-1",
          disabled && "cursor-not-allowed",
          className,
        )}
      >
        <CheckboxPrimitive.Root
          id={boxId}
          disabled={disabled}
          required={required}
          {...omitUndefined({
            checked,
            defaultChecked,
            onCheckedChange,
            name,
            value,
          })}
          aria-invalid={invalid || undefined}
          aria-describedby={
            invalid ? errorId : description ? descriptionId : undefined
          }
          className={cn(
            "flex h-icon-lg w-icon-lg shrink-0 items-center justify-center rounded-sm border-2 bg-surface",
            "transition-colors duration-100 ease-standard",
            REDUCED_MOTION_SAFE,
            FOCUS_RING,
            // A solid fill plus on-primary ink, never a washed tint: a pale
            // fill reads as "maybe checked" at a glance, which is the only
            // kind of reading this audience does.
            "data-[state=checked]:border-primary data-[state=checked]:bg-primary-solid data-[state=checked]:text-on-primary",
            "data-[state=indeterminate]:border-primary data-[state=indeterminate]:bg-primary-solid data-[state=indeterminate]:text-on-primary",
            "disabled:cursor-not-allowed disabled:border-disabled-border disabled:bg-disabled-bg disabled:text-disabled-text",
            invalid ? "border-error" : "border-line",
          )}
        >
          <CheckboxPrimitive.Indicator>
            {checked === "indeterminate" ||
            defaultChecked === "indeterminate" ? (
              <IndeterminateIcon />
            ) : (
              <CheckIcon />
            )}
          </CheckboxPrimitive.Indicator>
        </CheckboxPrimitive.Root>

        <span className="flex flex-col gap-1">
          <span
            className={cn(
              "text-body text-ink",
              disabled && "text-disabled-text",
            )}
          >
            {label}
            {required ? (
              <span className="text-error" aria-hidden="true">
                {" *"}
              </span>
            ) : null}
          </span>
          {description ? (
            <span
              id={descriptionId}
              className="text-caption text-ink-secondary"
            >
              {description}
            </span>
          ) : null}
        </span>
      </label>

      {invalid ? (
        <p id={errorId} role="alert" className="text-caption text-error">
          {error}
        </p>
      ) : null}
    </div>
  );
};
